// Growth › a lead's Email: one button, "Email", for the lead page's header. It
// opens the one-off compose (pages/signups/OutreachCompose.tsx, the same panel a
// signup's profile and a tenant's Email use) in a dialog, a bottom sheet on a
// phone, aimed at this lead, with the emails already sent or scheduled to it
// below.
//
//   <LeadEmailButton lead={lead} />          lead: { id, name } at least
//
// An email to a lead goes like any one-off, so Gmail files it as a note from a
// person, not a mailing (owner, November 4): signed, the logo, nothing after the
// signature, no unsubscribe link or headers. Real sending still refuses without
// GROWTH_MAILING_ADDRESS. A lead who replies asking to stop is suppressed. The lead's fields fill the
// merge fields, {{preview_url}} its live preview. Send now, or Schedule for a day
// and a time: a scheduled one sits on Growth › Schedule, where it can be removed
// or moved, until the server sends it. The owner's confirm in the compose is the
// approval; nothing here sends on its own.
//
// Self-contained on purpose: the lead page is redesigned separately and only
// places the button.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { MERGE_FIELDS } from '@/api/growthCampaignTypes'
import type { LeadOutreach, OutreachEmail } from '@/api/growthSignups'
import type { Lead } from '@/api/types'
import { Badge, Button, Dialog, ErrorState, SkeletonRows } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { sendErrorLabel } from '@/lib/growthCampaigns'
import { invalidateQueries, useQuery } from '@/lib/query'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_TONE } from '@/lib/signups'
import { OutreachCompose, type OutreachTarget } from '@/pages/signups/OutreachCompose'
import { reSubject, type ReplyTo } from '@/lib/conversation'

export interface LeadEmailButtonProps {
  /** The lead the email goes to. Only `id` and `name` are read; the address comes from the server. */
  lead: Pick<Lead, 'id' | 'name'>
  /** Button size, to sit with the header's other actions. Default `md`. */
  size?: 'sm' | 'md'
}

/** "Email": opens the compose for this lead in a dialog. */
export function LeadEmailButton({ lead, size = 'md' }: LeadEmailButtonProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size={size} onClick={() => setOpen(true)}>
        Email
      </Button>
      <LeadEmailDialog lead={lead} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function fieldsHint(previewUrl: string | null): string {
  return (
    `Merge fields: ${MERGE_FIELDS.map((f) => `{{${f}}}`).join(', ')}. The lead fills its own: league_name (its name), contact_name, first_name, city, state, sport, website` +
    `, and preview_url (${previewUrl ? 'its live preview' : 'it has no live preview yet, so give it a fallback or build one first'}). site_url has no value for a lead. ` +
    'A fallback fills a missing value: {{first_name|there}}. The signature goes under it.'
  )
}

function emailWhen(e: OutreachEmail): string {
  if (e.status === 'scheduled') return `Goes ${formatDateTime(e.scheduled_for)}`
  if (e.status === 'removed') return `Was set for ${formatDateTime(e.scheduled_for)}`
  return formatDateTime(e.sent_at ?? e.created_at)
}

/** The emails already sent or scheduled to this lead, newest first. An inquiry's page lists its replies with it too (`label`, `empty`). */
export function LeadEmailHistory({ emails, label = 'Emails to this lead', empty = 'No emails to this lead yet.' }: { emails: OutreachEmail[]; label?: string; empty?: string }) {
  if (emails.length === 0) return <p className="text-[13px] text-muted">{empty}</p>
  return (
    <ul aria-label={label} className="divide-y divide-line rounded-md border border-line">
      {emails.map((e) => (
        <li key={e.id} className="grid gap-1 px-3 py-2.5 text-[13px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 break-words font-semibold text-ink">{e.subject}</span>
            <Badge tone={EMAIL_STATUS_TONE[e.status]}>{EMAIL_STATUS_LABELS[e.status]}</Badge>
            {e.replied_at ? <Badge tone="good">Replied</Badge> : null}
            {e.bounced_at ? <Badge tone="bad">{e.bounce_kind === 'soft' ? 'Soft bounce' : 'Bounced'}</Badge> : null}
            {e.unsubscribed_at ? <Badge tone="outline">Unsubscribed</Badge> : null}
          </div>
          <p className="break-words text-xs text-muted">
            {emailWhen(e)} · signed by {e.signer_name}
            {e.status === 'dry_run' ? ' · sending was off, nothing left' : e.error ? ` · ${sendErrorLabel(e.error)}` : ''}
            {e.status === 'scheduled' ? (
              <>
                {' · '}
                <Link to="/growth/schedule" className="underline underline-offset-2 hover:text-accent">
                  On the Schedule
                </Link>
              </>
            ) : null}
          </p>
        </li>
      ))}
    </ul>
  )
}


function leadTarget(lead: LeadEmailButtonProps['lead'], data: LeadOutreach, reply: ReplyTo | null): OutreachTarget {
  const problem = data.problem
  const withReply = <T extends object>(draft: T) => (reply ? { ...draft, reply_to: reply.message_id } : draft)
  return {
    to: data.to,
    suppressed: problem?.code === 'suppressed' ? true : undefined,
    noAddress: 'This lead has no email address we can write to. Add one on the lead first.',
    problem: problem && problem.code !== 'suppressed' && problem.code !== 'no_address' ? problem.message : null,
    previewKey: ['growth', 'lead-outreach-preview', lead.id],
    preview: (draft, signal) => api.growth.outreach.previewLead(lead.id, withReply(draft), signal),
    send: (draft) => api.growth.outreach.sendLead(lead.id, withReply(draft)),
    ...(reply ? { start: { subject: reSubject(reply.subject), body: '' } } : {}),
    onSent: () => invalidateQueries('growth'),
    canSchedule: true,
    fieldsHint: fieldsHint(data.preview_url),
  }
}

/** The dialog itself, for a caller that wants its own trigger. */
export function LeadEmailDialog({ lead, open, onClose, reply = null }: { lead: LeadEmailButtonProps['lead']; open: boolean; onClose: () => void; reply?: ReplyTo | null }) {
  const data = useQuery(open ? ['growth', 'lead-outreach', lead.id] : null, (signal) => api.growth.outreach.lead(lead.id, signal))
  const options = useQuery(open ? ['signups', 'outreach-options'] : null, (signal) => api.growth.outreach.options(signal))
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={reply ? `Reply to ${lead.name}` : `Email ${lead.name}`}
      description={
        reply
          ? 'In the same thread, in their inbox and in our Gmail. From ScoreLM <contact@scorelm.com>, signed by one of the team, with nothing after the signature.'
          : "A 1:1 email from ScoreLM <contact@scorelm.com>, signed by one of the team. It ends at the signature, with no unsubscribe link, so it reads like a note. Send it now, or schedule it."
      }
    >
      <div className="grid gap-5">
        {data.status === 'error' && !data.data ? (
          <ErrorState error={data.error} title="Couldn't load this lead's email" onRetry={data.refetch} />
        ) : options.status === 'error' && !options.data ? (
          <ErrorState error={options.error} title="Couldn't load the compose panel" onRetry={options.refetch} />
        ) : !data.data || !options.data ? (
          <SkeletonRows rows={4} label="Loading the compose panel" />
        ) : (
          <>
            <OutreachCompose key={reply?.message_id ?? 'new'} bare target={leadTarget(lead, data.data, reply)} options={options.data} />
            <section className="grid gap-2">
              <h3 className="text-sm font-bold text-ink">Emails to this lead</h3>
              <LeadEmailHistory emails={data.data.emails} />
            </section>
          </>
        )}
      </div>
    </Dialog>
  )
}
